import React from "react"
import type { AvailabilityResult, ConsentType, ScreeningAppointmentType } from "./hooks"

export type BookingResultSource = "known_data" | "screening" | "follow_up" | "booking_recheck"
export type BookingScreen =
  | { screen: "consent"; consentStep: ConsentType }
  | { screen: "gating" }
  | { screen: "scheduling"; appointmentType: ScreeningAppointmentType }
  | { screen: "not_eligible" | "booked" }

/** Semantic signals only: no answers, vendor identifiers, tokens, or navigation URLs. */
export type BookingTrackingEvent =
  | { type: "screen"; view: BookingScreen }
  | {
      type: "initialized"
      appointmentType: ScreeningAppointmentType
      ineligible: boolean
      consentSkipped: boolean
      noQuestions: boolean
    }
  | { type: "consent_skipped" }
  | { type: "gating_skipped"; appointmentType: ScreeningAppointmentType }
  | { type: "gating_result"; result: "eligible" | "ineligible"; resultSource: BookingResultSource }
  | { type: "needs_consent" }
  // The search key is used only for exposure deduplication; it is never sent as metadata.
  | { type: "availability"; searchKey: string; outcome: AvailabilityResult["outcome"] | null }
  | { type: "success"; appointmentId: string; hostId: string; bookingAttemptNumber: number }
  | { type: "prepare"; appointmentId: string }

export type BookingTrackingCallbacks = {
  onEvent: (event: BookingTrackingEvent) => void
  onBookingSubmitted: (appointmentType: ScreeningAppointmentType) => number
}

const untracked: BookingTrackingCallbacks = { onEvent: () => undefined, onBookingSubmitted: () => 0 }
export const BookingTrackingContext = React.createContext<BookingTrackingCallbacks>(untracked)

export function useBookingTracking(): BookingTrackingCallbacks {
  const tracking = React.useContext(BookingTrackingContext)
  return React.useMemo<BookingTrackingCallbacks>(
    () => ({
      onEvent: (event) => {
        try {
          tracking.onEvent(event)
        } catch {
          // Optional instrumentation must not interrupt booking or confirmation.
        }
      },
      onBookingSubmitted: (appointmentType) => {
        try {
          return tracking.onBookingSubmitted(appointmentType)
        } catch {
          return 0
        }
      }
    }),
    [tracking]
  )
}

export function useTrackBookingScreenView(view: BookingScreen | null): void {
  const { onEvent } = useBookingTracking()
  // Values, not object identity, define a screen. The host additionally guards equivalent remounts.
  const screen = view?.screen
  const consentStep = view?.screen === "consent" ? view.consentStep : undefined
  const appointmentType = view?.screen === "scheduling" ? view.appointmentType : undefined
  React.useLayoutEffect(() => {
    if (screen === "consent" && consentStep) onEvent({ type: "screen", view: { screen, consentStep } })
    else if (screen === "scheduling" && appointmentType) onEvent({ type: "screen", view: { screen, appointmentType } })
    else if (screen === "gating" || screen === "not_eligible" || screen === "booked")
      onEvent({ type: "screen", view: { screen } })
  }, [onEvent, screen, consentStep, appointmentType])
}
